import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  PropsWithChildren,
  useEffect,
} from 'react';
import { Context } from './types';
import { useBoolState } from 'common/hooks';
import { useOperations as useFuelCardsOperations } from 'store/features/fuelCards';

import {
  useOperations,
  useOperations as useStationsOperations,
} from 'store/features/stations';
import {
  useFilter,
  useRouteFilter,
} from 'store/features/stations/selectors/filter';
import { useRoutesContext } from 'common/context/routes';
import useUpdateActiveStationsByActiveRoute from 'pages/Map/hooks/useUpdateActiveStationsByActiveRoute';
import { useSelector } from 'react-redux';
import { selectors as stationsSelectors } from 'store/features/stations';
import { useActiveEvent } from 'store/features/events/selectors/activeEvent';
import useRadioState from 'common/hooks/useRadioState';
import {
  AvailabilityType,
  DEFAULT_AVAILABILITY,
  DEFAULT_AVAILABILITY_STATUS,
  FuelType,
} from 'pages/Map/components/FuelAvailabilityButton/constants';
import { useOperations as useStationOperations } from 'store/features/stations';
import { EMPTY_STRING } from 'common/constants';
import { RestrictionStatus } from 'dataSources/Typicode/stations';

export const FuelAvailabilityContext = createContext({} as Context);
export const useFuelAvailabilityContext = () =>
  useContext(FuelAvailabilityContext);

export const FuelAvailabilityProvider = ({ children }: PropsWithChildren) => {
  const { setRestrictions } = useOperations();
  const [showFuelModal, , , onHideFuelModal, onShowFuelModal] =
    useBoolState(false);
  const [fuel, onChangeFuel, setFuel] = useRadioState();
  const [availability, onChangeAvailability, setAvailability] =
    useRadioState(DEFAULT_AVAILABILITY);

  const [availabilityStatuses, setAvailabilityStatuses] = useState<
    Array<RestrictionStatus>
  >([DEFAULT_AVAILABILITY_STATUS]);

  const [bufferedActiveRouteRestrictions, setBufferedActiveRouteRestrictions] =
    useState<{
      fuel: FuelType | string;
      availability: AvailabilityType;
      availabilityStatuses: Array<RestrictionStatus>;
    }>({
      fuel: EMPTY_STRING,
      availability: DEFAULT_AVAILABILITY,
      availabilityStatuses: [DEFAULT_AVAILABILITY_STATUS],
    });

  const [fuelSelected, setFuelSelected] = useState<FuelType | string>(
    EMPTY_STRING,
  );
  const [availabilitySelected, setAvailabilitySelected] =
    useState<AvailabilityType>(DEFAULT_AVAILABILITY);
  const [availabilityStatusesSelected, setAvailabilityStatusesSelected] =
    useState<Array<RestrictionStatus>>([DEFAULT_AVAILABILITY_STATUS]);

  const { setActiveFuelCard } = useFuelCardsOperations();

  const filterModalData = useFilter();
  const builderRouteFilterData = useRouteFilter();
  const { activeRoute, setTriggeredRecalculate } = useRoutesContext();
  const handleUpdateActiveRouteStations =
    useUpdateActiveStationsByActiveRoute();
  const { setStations } = useStationsOperations();
  const stationsMap = useSelector(stationsSelectors.allStationsMap);
  const activeEvent = useActiveEvent();
  const { getStations } = useStationOperations();

  const restoreState = () => {
    const fuelValue = bufferedActiveRouteRestrictions.fuel;
    const availabilityValue = bufferedActiveRouteRestrictions.availability;
    const availabilityStatusesValue =
      bufferedActiveRouteRestrictions.availabilityStatuses;

    setFuel(fuelValue);
    setFuelSelected(fuelValue);
    setAvailability(availabilityValue);
    setAvailabilitySelected(availabilityValue);
    setAvailabilityStatuses(availabilityStatusesValue);
    setRestrictions({
      serviceNtType: fuelValue,
      mode: availabilityValue,
      statuses: availabilityStatusesValue,
    });
  };

  useEffect(() => {
    if (activeRoute) {
      setBufferedActiveRouteRestrictions({
        fuel: fuelSelected,
        availability: availabilitySelected,
        availabilityStatuses: availabilityStatuses,
      });
    } else {
      restoreState();
    }
  }, [activeRoute]);

  const handleUpdateResults = useCallback(
    async ({
      fuel,
      availability,
      availabilityStatuses,
      callback,
    }: {
      fuel: FuelType | string;
      availability: AvailabilityType;
      availabilityStatuses: Array<RestrictionStatus>;
      callback?: () => void;
    }) => {
      setFuelSelected(fuel);
      setAvailabilitySelected(availability as AvailabilityType);
      setAvailabilityStatusesSelected(availabilityStatuses);
      setRestrictions({
        serviceNtType: fuel,
        mode: availability as AvailabilityType,
        statuses: availabilityStatuses,
      });
      onHideFuelModal();

      const filter = Object.keys(builderRouteFilterData).length
        ? builderRouteFilterData
        : filterModalData;

      if (activeRoute) {
        setTriggeredRecalculate(true);
      } else {
        getStations(filter);
      }
      callback?.();
    },
    [
      fuel,
      availability,
      availabilityStatuses,
      setFuelSelected,
      setAvailabilitySelected,
      setAvailabilityStatusesSelected,
      activeEvent.data,
      stationsMap,
      setStations,
      setActiveFuelCard,
      filterModalData,
      builderRouteFilterData,
      activeRoute,
      handleUpdateActiveRouteStations,
    ],
  );

  const value = useMemo(
    () => ({
      showFuelModal,
      onHideFuelModal,
      onShowFuelModal,
      handleUpdateResults,
      fuel,
      onChangeFuel,
      setFuel,
      availability,
      onChangeAvailability,
      setAvailability,
      fuelSelected,
      setFuelSelected,
      availabilitySelected,
      setAvailabilitySelected,
      availabilityStatuses,
      setAvailabilityStatuses,
      availabilityStatusesSelected,
      setAvailabilityStatusesSelected,
    }),
    [
      showFuelModal,
      onHideFuelModal,
      onShowFuelModal,
      handleUpdateResults,
      fuel,
      onChangeFuel,
      setFuel,
      availability,
      onChangeAvailability,
      setAvailability,
      fuelSelected,
      setFuelSelected,
      availabilitySelected,
      setAvailabilitySelected,
      availabilityStatuses,
      setAvailabilityStatuses,
      availabilityStatusesSelected,
      setAvailabilityStatusesSelected,
    ],
  );

  return (
    <FuelAvailabilityContext.Provider value={value}>
      {children}
    </FuelAvailabilityContext.Provider>
  );
};

export default FuelAvailabilityProvider;
